import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import { BarChart3, TrendingUp, Trophy, Calendar, Gamepad2, Award, Star, ExternalLink, Gem, Clock, Flame, Moon, Sun, Target, Zap, PieChart as PieChartIcon, Tag, ChevronLeft, ChevronRight, Filter } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ComposedChart,
  Area,
  CartesianGrid,
  Legend,
  PieChart,
  Pie,
  LineChart,
  Line,
} from "recharts";
import Heatmap from "@/components/Heatmap";
import PlatformBadge from "@/components/PlatformBadge";
import StatsDrilldownDialog from "@/components/StatsDrilldownDialog";
import BestDayDialog from "@/components/BestDayDialog";
import GenreDetailDialog from "@/components/GenreDetailDialog";
import { format } from "date-fns";
import { AdBanner } from "@/components/AdSense";
import ProGate from "@/components/ProGate";
import { StatsSkeleton } from "@/components/Skeleton";
import confetti from "canvas-confetti";

const API = `${getApiUrl()}/api`;

const PLATFORM_COLORS = {
  xbox: "#107C10",
  playstation: "#2E6DB4",
  steam: "#66C0F4",
  epic: "#FFFFFF",
};

const PLATFORM_ABBRS = {
  xbox: "GS",
  playstation: "XP",
  steam: "XP",
  epic: "XP",
};

const BEST_DAYS_COLORS = {
  score: "#F59E0B",
  unlocks: "#10B981",
  completions: "#8B5CF6",
};

// Custom tooltip for area chart - moved outside component to avoid re-creation on every render
const CustomAreaTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload || {};
    return (
      <div className="bg-black/90 border border-white/10 rounded-lg px-3 py-2">
        <p className="text-white font-medium">{label}</p>
        <p className="text-green-400 text-sm">{data.achievements} achievements</p>
        <p className="text-yellow-400 text-sm">{data.score?.toLocaleString()} score</p>
        {data.gameCompletions > 0 && (
          <p className="text-red-400 text-sm">{data.gameCompletions} game{data.gameCompletions > 1 ? 's' : ''} 100%</p>
        )}
        {data.dlcCompletions > 0 && (
          <p className="text-blue-400 text-sm">{data.dlcCompletions} DLC{data.dlcCompletions > 1 ? 's' : ''} 100%</p>
        )}
      </div>
    );
  }
  return null;
};

// Rich tooltip for Active Times (hourly)
const ActiveTimesTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const d = payload[0]?.payload || {};
    const h = d.hour;
    const ampm = h >= 12 ? "PM" : "AM";
    const hr = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return (
      <div className="bg-black/90 border border-white/10 rounded-lg px-3 py-2">
        <p className="text-white font-medium">{hr}:00 {ampm} - {hr}:59 {ampm}</p>
        <p className="text-cyan-400 text-sm">{(d.count || 0).toLocaleString()} achievements</p>
        <p className="text-yellow-400 text-sm">{(d.score || 0).toLocaleString()} score</p>
      </div>
    );
  }
  return null;
};

// Rich tooltip for Day of Week
const DayOfWeekTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const d = payload[0]?.payload || {};
    return (
      <div className="bg-black/90 border border-white/10 rounded-lg px-3 py-2">
        <p className="text-white font-medium">{d.day}</p>
        <p className="text-blue-400 text-sm">{(d.count || 0).toLocaleString()} achievements</p>
        <p className="text-yellow-400 text-sm">{(d.score || 0).toLocaleString()} score</p>
      </div>
    );
  }
  return null;
};

// Rich tooltip for Seasonal
const SeasonalTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const d = payload[0]?.payload || {};
    const months = { Winter: "Dec-Feb", Spring: "Mar-May", Summer: "Jun-Aug", Fall: "Sep-Nov" };
    return (
      <div className="bg-black/90 border border-white/10 rounded-lg px-3 py-2">
        <p className="text-white font-medium">{d.season}</p>
        <p className="text-white/40 text-xs">{months[d.season] || ""}</p>
        <p className="text-green-400 text-sm">{(d.count || 0).toLocaleString()} achievements</p>
        <p className="text-yellow-400 text-sm">{(d.score || 0).toLocaleString()} score</p>
      </div>
    );
  }
  return null;
};

// Rich tooltip for Genre charts
const GenreTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const d = payload[0]?.payload || {};
    return (
      <div className="bg-black/90 border border-white/10 rounded-lg px-3 py-2">
        <p className="text-white font-medium">{d.genre}</p>
        <p className="text-amber-400 text-sm">{(d.count || 0).toLocaleString()} game{d.count !== 1 ? "s" : ""}</p>
        <p className="text-green-400 text-sm">{(d.achievements || 0).toLocaleString()} achievements</p>
        {(d.completions || 0) > 0 && (
          <p className="text-purple-400 text-sm">{d.completions} completion{d.completions !== 1 ? "s" : ""}</p>
        )}
      </div>
    );
  }
  return null;
};

// Rich tooltip for Score Distribution
const ScoreDistTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const d = payload[0]?.payload || {};
    const avg = d.count > 0 ? Math.round((d.totalScore || 0) / d.count) : 0;
    return (
      <div className="bg-black/90 border border-white/10 rounded-lg px-3 py-2">
        <p className="text-white font-medium">{d.range} points</p>
        <p className="text-pink-400 text-sm">{(d.count || 0).toLocaleString()} achievements</p>
        <p className="text-yellow-400 text-sm">{(d.totalScore || 0).toLocaleString()} total score</p>
        <p className="text-white/40 text-xs">~{avg} avg per achievement</p>
      </div>
    );
  }
  return null;
};

// Milestone label helpers
const platName = (p) => {
  if (p === "playstation") return "PlayStation";
  if (!p || p === "unknown") return "Unknown";
  return p.charAt(0).toUpperCase() + p.slice(1);
};

// Convert slug-format system names to display names (e.g. "xbox-360" → "Xbox 360")
const sysDisplayName = (sys) => {
  if (!sys) return "";
  // Already a proper display name (contains uppercase or spaces)?
  if (sys !== sys.toLowerCase() || sys.includes(" ")) return sys;
  // Known slug → display name mappings
  const slugMap = {
    "xbox-360": "Xbox 360",
    "xbox-one": "Xbox One",
    "xbox-series": "Xbox Series X|S",
    "xbox-series-xs": "Xbox Series X|S",
    "original-xbox": "Original Xbox",
    "playstation-5": "PlayStation 5",
    "playstation-4": "PlayStation 4",
    "playstation-3": "PlayStation 3",
    "ps-vita": "PS Vita",
    "ps5": "PS5",
    "ps4": "PS4",
    "ps3": "PS3",
    "psp": "PSP",
    "windows-10": "Windows 10",
    "windows-8": "Windows 8",
    "pc": "PC",
    "steam-windows": "Windows",
    "steam-mac": "macOS",
    "steam-linux": "Linux",
  };
  const lower = sys.toLowerCase();
  if (slugMap[lower]) return slugMap[lower];
  // Fallback: capitalize each word from slug
  return sys.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
};

const sysAbbrev = (sys) => {
  if (!sys) return "?";
  const s = (sys || "").toLowerCase().replace(/-/g, " ");
  if (s.includes("360")) return "360";
  if (s.includes("series")) return "X|S";
  if (s === "xbox one") return "ONE";
  if (s === "original xbox") return "OG";
  if (s === "playstation 5" || s === "ps5") return "PS5";
  if (s === "playstation 4" || s === "ps4") return "PS4";
  if (s === "playstation 3" || s === "ps3") return "PS3";
  if (s.includes("vita")) return "VTA";
  if (s.includes("psp")) return "PSP";
  if (s.includes("deck")) return "SDK";
  if (s === "pc") return "PC";
  if (s.includes("windows 10")) return "W10";
  if (s.includes("windows 8")) return "W8";
  if (s.includes("windows")) return "WIN";
  if (s.includes("macos")) return "MAC";
  if (s.includes("linux")) return "LNX";
  return sys.slice(0, 3).toUpperCase();
};

const achMilestoneLabel = (m) => {
  if (m.system) {
    const sys = sysDisplayName(m.system);
    return m.target === 1
      ? `1st ${sys} Achievement`
      : `${sys} #${m.target.toLocaleString()}`;
  }
  if (m.platform) {
    return m.target === 1
      ? `1st ${platName(m.platform)} Achievement`
      : `${platName(m.platform)} #${m.target.toLocaleString()}`;
  }
  return `Achievement #${m.target.toLocaleString()}`;
};

const scoreMilestoneLabel = (m) => {
  if (m.system) {
    return `${m.target.toLocaleString()} ${sysDisplayName(m.system)} Score`;
  }
  if (m.platform) {
    return `${m.target.toLocaleString()} ${platName(m.platform)} Score`;
  }
  return `${m.target.toLocaleString()} Score`;
};

const compMilestoneLabel = (m) => {
  if (m.scope === "dlc") return `DLC Completion #${m.target.toLocaleString()}`;
  if (m.scope === "system" && m.system) {
    const sys = sysDisplayName(m.system);
    return m.target === 1
      ? `1st ${sys} Completion`
      : `${sys} #${m.target.toLocaleString()}`;
  }
  if (m.scope === "platform") {
    return m.target === 1
      ? `1st ${platName(m.platform)} Completion`
      : `${platName(m.platform)} #${m.target.toLocaleString()}`;
  }
  return `Completion #${m.target.toLocaleString()}`;
};

const tagMilestoneLabel = (m) => {
  const label = m.tagLabel || m.tag || "Tag";
  return `${m.target.toLocaleString()} ${label}`;
};

const nextMilestoneLabel = (nm, type) => {
  if (!nm) return "";
  if (nm.scope === "dlc") return `DLC #${nm.target.toLocaleString()}`;
  if (nm.tag) return `${nm.target.toLocaleString()} ${nm.tagLabel || nm.tag}`;
  if (nm.system) return `${sysDisplayName(nm.system)} #${nm.target.toLocaleString()}`;
  if (nm.platform) return `${platName(nm.platform)} #${nm.target.toLocaleString()}`;
  if (type === "score") return `${nm.target.toLocaleString()} Score`;
  return `#${nm.target.toLocaleString()}`;
};

export default function Statistics() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topGamesCategory, setTopGamesCategory] = useState("score");
  const [timeRange, setTimeRange] = useState("12months");
  const [visibleSeries, setVisibleSeries] = useState({
    achievements: true,
    score: true,
    gameCompletions: true,
    dlcCompletions: true,
  });
  const [bestDaysCategory, setBestDaysCategory] = useState("score");
  const [bestDaysData, setBestDaysData] = useState([]);
  const [bestDaysDialogOpen, setBestDaysDialogOpen] = useState(false);
  const [fullBestDaysData, setFullBestDaysData] = useState([]);
  const [topGamesDialogOpen, setTopGamesDialogOpen] = useState(false);
  
  // Completion Speed Stats
  const [completionSpeedStats, setCompletionSpeedStats] = useState(null);
  const [completionSpeedPlatform, setCompletionSpeedPlatform] = useState("all");
  const [completionSpeedDialogOpen, setCompletionSpeedDialogOpen] = useState(false);
  const [completionSpeedDialogType, setCompletionSpeedDialogType] = useState("fastest"); // "fastest" or "slowest"
  
  // Platform and System filters
  const [platforms, setPlatforms] = useState([]);
  const [systems, setSystems] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState("all");
  const [selectedSystem, setSelectedSystem] = useState("all");
  
  // New stats
  const [activeTimesData, setActiveTimesData] = useState(null);
  const [genreData, setGenreData] = useState([]);
  const [completionsByPlatform, setCompletionsByPlatform] = useState(null);
  
  // Extended stats
  const [streakData, setStreakData] = useState(null);
  const [insightsData, setInsightsData] = useState(null);
  const [distributionData, setDistributionData] = useState(null);

  // Drilldown dialog state
  const [drilldown, setDrilldown] = useState({ open: false, chartType: null, value: null });
  const [bestDayOpen, setBestDayOpen] = useState(false);
  const [bestDayDate, setBestDayDate] = useState(null);
  const [genreOpen, setGenreOpen] = useState(false);
  const [genreName, setGenreName] = useState(null);
  const tzOffset = new Date().getTimezoneOffset() * -1;
  const tzName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const openDrilldown = (chartType, value) => setDrilldown({ open: true, chartType, value });
  const closeDrilldown = () => setDrilldown({ open: false, chartType: null, value: null });

  // Milestone hatching: track newly reached milestones
  const [hatchedMilestones, setHatchedMilestones] = useState(new Set());
  const hatchConfettiFired = useRef(new Set());

  const fetchCompletionSpeedStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append("limit", "10");
      if (completionSpeedPlatform && completionSpeedPlatform !== "all") {
        params.append("platform", completionSpeedPlatform);
      }
      const response = await axios.get(`${API}/stats/completion-speed?${params}`);
      setCompletionSpeedStats(response.data);
    } catch (error) {
      console.error("Failed to fetch completion speed stats:", error);
    }
  }, [completionSpeedPlatform]);

  const fetchPlatforms = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/platforms`);
      setPlatforms(response.data || []);
    } catch (error) {
      console.error("Failed to fetch platforms:", error);
      setPlatforms([]);
    }
  }, []);

  const fetchSystems = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/systems`);
      setSystems(response.data || []);
    } catch (error) {
      console.error("Failed to fetch systems:", error);
      setSystems([]);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("timeRange", "all");
      params.append("tz_offset", String(new Date().getTimezoneOffset() * -1));
      if (selectedPlatform !== "all") params.append("platform", selectedPlatform);
      if (selectedSystem !== "all") params.append("system", selectedSystem);
      
      const [statsRes, gamesRes] = await Promise.all([
        axios.get(`${API}/stats?${params.toString()}`),
        axios.get(`${API}/games?${params.toString()}`),
      ]);
      setStats(statsRes.data);
      setGames(gamesRes.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedPlatform, selectedSystem]);

  const fetchBestDays = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append("category", bestDaysCategory);
      params.append("limit", "10");
      if (selectedPlatform !== "all") params.append("platform", selectedPlatform);
      if (selectedSystem !== "all") params.append("system", selectedSystem);
      
      const response = await axios.get(`${API}/stats/best-days?${params.toString()}`);
      setBestDaysData(response.data);
    } catch (error) {
      console.error("Failed to fetch best days:", error);
    }
  }, [bestDaysCategory, selectedPlatform, selectedSystem]);

  // Fetch active times, genres, completions, streaks, insights, distributions
  const fetchNewStats = useCallback(async () => {
    try {
      const tzOff = new Date().getTimezoneOffset() * -1;
      const platParam = selectedPlatform !== "all" ? `&platform=${selectedPlatform}` : "";
      const [timesRes, genresRes, complRes, streakRes, insightsRes, distRes] = await Promise.allSettled([
        axios.get(`${API}/stats/active-times?tz_offset=${tzOff}${platParam}`),
        axios.get(`${API}/stats/genres?${platParam.replace("&", "")}`),
        axios.get(`${API}/stats/completions-by-platform`),
        axios.get(`${API}/stats/streaks-milestones?tz_offset=${tzOff}&tz_name=${encodeURIComponent(tzName)}`),
        axios.get(`${API}/stats/insights?tz_offset=${tzOff}`),
        axios.get(`${API}/stats/distributions?tz_offset=${tzOff}`),
      ]);
      if (timesRes.status === "fulfilled") setActiveTimesData(timesRes.value.data);
      if (genresRes.status === "fulfilled") setGenreData(genresRes.value.data);
      if (complRes.status === "fulfilled") setCompletionsByPlatform(complRes.value.data);
      if (streakRes.status === "fulfilled") setStreakData(streakRes.value.data);
      if (insightsRes.status === "fulfilled") setInsightsData(insightsRes.value.data);
      if (distRes.status === "fulfilled") setDistributionData(distRes.value.data);
    } catch (err) {
      console.error("Failed to fetch new stats:", err);
    }
  }, [selectedPlatform]);

  // useEffect hooks - must be after function definitions
  useEffect(() => {
    fetchPlatforms();
    fetchSystems();
  }, [fetchPlatforms, fetchSystems]);

  useEffect(() => {
    fetchData();
    fetchNewStats();
  }, [fetchData, fetchNewStats]);

  useEffect(() => {
    fetchBestDays();
  }, [fetchBestDays]);

  useEffect(() => {
    fetchCompletionSpeedStats();
  }, [fetchCompletionSpeedStats]);

  // Detect newly reached milestones by comparing with localStorage
  useEffect(() => {
    if (!streakData?.milestones) return;
    const storageKey = "gameorang_seen_milestones";
    const allReached = (streakData.milestones || []).map(m => `${m.type}-${m.target}-${m.platform || ""}-${m.system || ""}-${m.scope || ""}`);
    const compReached = (streakData.completionMilestones || []).map(m => `comp-${m.target}-${m.platform || ""}-${m.system || ""}-${m.scope || ""}`);
    const currentKeys = new Set([...allReached, ...compReached]);

    try {
      const seenRaw = localStorage.getItem(storageKey);
      const seen = seenRaw ? new Set(JSON.parse(seenRaw)) : new Set();
      const newlyReached = new Set();
      currentKeys.forEach(k => { if (!seen.has(k)) newlyReached.add(k); });
      if (newlyReached.size > 0) {
        setHatchedMilestones(newlyReached);
      }
      localStorage.setItem(storageKey, JSON.stringify([...currentKeys]));
    } catch {
      // First visit or corrupted storage — store current state, no animation
      localStorage.setItem(storageKey, JSON.stringify([...currentKeys]));
    }
  }, [streakData]);

  // Helper to build a milestone key for hatching detection
  const makeMilestoneKey = (m, type) => {
    if (type === "comp") return `comp-${m.target}-${m.platform || ""}-${m.system || ""}-${m.scope || ""}`;
    return `${m.type || type}-${m.target}-${m.platform || ""}-${m.system || ""}-${m.scope || ""}`;
  };

  const fireHatchConfetti = (element) => {
    if (!element) return;
    const rect = element.getBoundingClientRect();
    const x = (rect.left + rect.width / 2) / window.innerWidth;
    const y = (rect.top + rect.height / 2) / window.innerHeight;
    confetti({
      particleCount: 50,
      spread: 60,
      origin: { x, y },
      colors: ["#8B5CF6", "#F59E0B", "#10B981", "#EC4899", "#3B82F6"],
      scalar: 0.8,
      gravity: 1.2,
      ticks: 120,
    });
  };

  // Ref callback for hatched milestone elements - fires confetti once on mount
  const hatchRef = (key) => (el) => {
    if (el && !hatchConfettiFired.current.has(key)) {
      hatchConfettiFired.current.add(key);
      setTimeout(() => fireHatchConfetti(el), 400);
    }
  };

  const openBestDaysDialog = async () => {
    try {
      const params = new URLSearchParams();
      params.append("category", bestDaysCategory);
      params.append("limit", "50");
      if (selectedPlatform !== "all") params.append("platform", selectedPlatform);
      if (selectedSystem !== "all") params.append("system", selectedSystem);
      
      const response = await axios.get(`${API}/stats/best-days?${params.toString()}`);
      setFullBestDaysData(response.data);
      setBestDaysDialogOpen(true);
    } catch (error) {
      console.error("Failed to fetch full best days:", error);
    }
  };

  // Get systems filtered by selected platform
  const filteredSystems = Array.isArray(systems) ? (
    selectedPlatform === "all" 
      ? systems 
      : systems.filter(s => s.platformId === selectedPlatform)
  ) : [];

  const getBestDaysLabel = () => {
    switch (bestDaysCategory) {
      case "score": return "Score";
      case "unlocks": return "Achievements";
      case "completions": return "Completions";
      default: return "Score";
    }
  };

  const formatBestDaysValue = (value) => {
    switch (bestDaysCategory) {
      case "score": return `${value.toLocaleString()} pts`;
      case "unlocks": return `${value} achievements`;
      case "completions": return `${value} games`;
      default: return value;
    }
  };

  const getScoreAbbr = (platformId) => {
    return PLATFORM_ABBRS[platformId] || "XP";
  };

  // Format duration for display
  const formatDuration = (hours, days) => {
    if (days >= 365) {
      const years = Math.floor(days / 365);
      const remainingDays = Math.floor(days % 365);
      return `${years}y ${remainingDays}d`;
    } else if (days >= 30) {
      const months = Math.floor(days / 30);
      const remainingDays = Math.floor(days % 30);
      return `${months}mo ${remainingDays}d`;
    } else if (days >= 1) {
      return `${Math.floor(days)}d ${Math.floor((days % 1) * 24)}h`;
    } else if (hours >= 1) {
      return `${Math.floor(hours)}h ${Math.floor((hours % 1) * 60)}m`;
    } else {
      return `${Math.floor(hours * 60)}m`;
    }
  };

  // Toggle series visibility when clicking legend
  const handleLegendClick = (dataKey) => {
    setVisibleSeries(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey],
    }));
  };

  // Custom legend with click handlers
  const renderCustomLegend = (props) => {
    const { payload } = props;
    return (
      <div className="flex flex-wrap justify-center gap-4 pt-2">
        {payload.map((entry, index) => {
          const dataKey = entry.dataKey;
          const isVisible = visibleSeries[dataKey];
          return (
            <button
              key={`legend-${index}`}
              onClick={() => handleLegendClick(dataKey)}
              className={`flex items-center gap-2 px-2 py-1 rounded transition-all ${
                isVisible ? 'opacity-100' : 'opacity-40'
              } hover:bg-white/10`}
            >
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-white/70 text-sm">{entry.value}</span>
            </button>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return <StatsSkeleton />;
  }

  const platformData = stats?.platformBreakdown
    ? Object.entries(stats.platformBreakdown).map(([platform, data]) => ({
        name: platform.charAt(0).toUpperCase() + platform.slice(1),
        platform,
        score: data.score,
        count: data.count,
        color: PLATFORM_COLORS[platform] || "#7C3AED",
      }))
    : [];

  // Pie chart data for achievement distribution (sorted by value)
  const pieData = platformData.filter(p => p.count > 0).sort((a, b) => b.count - a.count);

  // Get filtered monthly data based on time range
  const getFilteredMonthlyData = () => {
    const allData = stats?.monthlyStats || [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    let filteredData;
    
    switch (timeRange) {
      case "1month":
        filteredData = allData.slice(-1);
        break;
      case "3months":
        filteredData = allData.slice(-3);
        break;
      case "6months":
        filteredData = allData.slice(-6);
        break;
      case "12months":
        filteredData = allData.slice(-12);
        break;
      case "thisYear":
        filteredData = allData.filter(item => item.month.startsWith(String(currentYear)));
        break;
      case "lastYear":
        filteredData = allData.filter(item => item.month.startsWith(String(currentYear - 1)));
        break;
      case "5years":
        filteredData = allData.filter(item => {
          const year = parseInt(item.month.split("-")[0]);
          return year >= currentYear - 5;
        });
        break;
      case "all":
        filteredData = allData;
        break;
      default:
        filteredData = allData.slice(-12);
    }

    return filteredData.map(item => {
      const date = new Date(item.month + "-01");
      let label;
      
      // Format label based on data length
      if (filteredData.length <= 12) {
        label = date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
      } else if (filteredData.length <= 24) {
        label = date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
      } else {
        // For longer ranges, show year only for January or use abbreviated format
        label = date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
      }

      return {
        month: label,
        fullMonth: item.month,
        achievements: item.count,
        score: item.score,
        gameCompletions: item.gameCompletions || 0,
        dlcCompletions: item.dlcCompletions || 0,
      };
    });
  };

  const monthlyData = getFilteredMonthlyData();

  const completionPct = stats?.completionPercentage || 0;

  // Top games data - changes based on selected category
  const getTopGamesData = () => {
    const baseData = games.map((g) => ({
      id: g.id,
      name: g.name.length > 15 ? g.name.slice(0, 15) + "..." : g.name,
      fullName: g.name,
      platform: g.platform,
      score: g.earnedScore || 0,
      unlocks: g.unlockedAchievements || 0,
      completion: g.totalAchievements > 0 ? Math.round((g.unlockedAchievements / g.totalAchievements) * 100) : 0,
    }));

    switch (topGamesCategory) {
      case "unlocks":
        return baseData
          .filter((g) => g.unlocks > 0)
          .sort((a, b) => b.unlocks - a.unlocks)
          .slice(0, 10);
      case "completion":
        return baseData
          .filter((g) => g.completion > 0)
          .sort((a, b) => b.completion - a.completion)
          .slice(0, 10);
      case "score":
      default:
        return baseData
          .filter((g) => g.score > 0)
          .sort((a, b) => b.score - a.score)
          .slice(0, 10);
    }
  };

  const topGamesData = getTopGamesData();
  
  // Full top games data for dialog (50 games)
  const getFullTopGamesData = () => {
    const baseData = games.map((g) => ({
      id: g.id,
      name: g.name,
      fullName: g.name,
      platform: g.platform,
      boxArtUrl: g.boxArtUrl,
      score: g.earnedScore || 0,
      totalScore: g.totalScore || 0,
      unlocks: g.unlockedAchievements || 0,
      totalAchievements: g.totalAchievements || 0,
      completion: g.totalAchievements > 0 ? Math.round((g.unlockedAchievements / g.totalAchievements) * 100) : 0,
    }));

    switch (topGamesCategory) {
      case "unlocks":
        return baseData
          .filter((g) => g.unlocks > 0)
          .sort((a, b) => b.unlocks - a.unlocks)
          .slice(0, 50);
      case "completion":
        return baseData
          .filter((g) => g.completion > 0)
          .sort((a, b) => b.completion - a.completion)
          .slice(0, 50);
      case "score":
      default:
        return baseData
          .filter((g) => g.score > 0)
          .sort((a, b) => b.score - a.score)
          .slice(0, 50);
    }
  };

  const getTopGamesCategoryLabel = () => {
    switch (topGamesCategory) {
      case "unlocks": return "Unlocks";
      case "completion": return "Completion";
      default: return "Score";
    }
  };

  const formatTopGamesValue = (game) => {
    switch (topGamesCategory) {
      case "unlocks": return `${game.unlocks} achievements`;
      case "completion": return `${game.completion}%`;
      default: return `${game.score.toLocaleString()} pts`;
    }
  };
  
  const getDataKey = () => {
    switch (topGamesCategory) {
      case "unlocks": return "unlocks";
      case "completion": return "completion";
      default: return "score";
    }
  };

  const getTooltipFormatter = () => {
    switch (topGamesCategory) {
      case "unlocks": return (value, name, props) => [`${value} achievements`, props.payload.fullName];
      case "completion": return (value, name, props) => [`${value}%`, props.payload.fullName];
      default: return (value, name, props) => [`${value.toLocaleString()} pts`, props.payload.fullName];
    }
  };

  const getXAxisDomain = () => {
    if (topGamesCategory === "completion") return [0, 100];
    return undefined;
  };

  return (
    <div data-testid="statistics-page" className="space-y-6 max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-white tracking-tight">
            Statistics
          </h1>
          <p className="text-white/50 mt-1">Deep dive into your gaming achievements</p>
        </div>
        
        {/* Platform & System Filters */}
        <div className="flex flex-wrap gap-2">
          <Select value={selectedPlatform} onValueChange={(v) => {
            setSelectedPlatform(v);
            setSelectedSystem("all");
          }}>
            <SelectTrigger className="w-[130px] bg-black/50 border-white/10 text-white text-sm">
              <SelectValue placeholder="Platform" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-white/10">
              <SelectItem value="all">All Platforms</SelectItem>
              {platforms.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: p.color }} />
                    {p.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={selectedSystem} onValueChange={setSelectedSystem}>
            <SelectTrigger className="w-[130px] bg-black/50 border-white/10 text-white text-sm">
              <SelectValue placeholder="System" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-white/10">
              <SelectItem value="all">All Systems</SelectItem>
              {filteredSystems.map((s) => (
                <SelectItem key={s.id} value={s.name}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {(selectedPlatform !== "all" || selectedSystem !== "all") && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedPlatform("all");
                setSelectedSystem("all");
              }}
              className="text-white/50 hover:text-white hover:bg-white/10"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* ============================================================ */}
      {/* SECTION 1: COMPLETION & PROGRESS                              */}
      {/* ============================================================ */}
      <h2 className="text-lg font-heading text-white/60 tracking-wide uppercase mt-2">Completion & Progress</h2>

      {/* Overall Completion + Backlog Meter */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-[#0A0A0A] border-white/5">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
              <Award className="w-5 h-5 text-purple-400" />
              Overall Completion
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center">
              <div className="relative w-40 h-40">
                <svg className="w-full h-full transform -rotate-90">
                  <circle cx="80" cy="80" r="70" stroke="#1F1F1F" strokeWidth="12" fill="none" />
                  <circle cx="80" cy="80" r="70" stroke="url(#progressGradient)" strokeWidth="12" fill="none" strokeLinecap="round" strokeDasharray={`${completionPct * 4.4} 440`} className="transition-all duration-1000" />
                  <defs>
                    <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#8B5CF6" />
                      <stop offset="100%" stopColor="#EC4899" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-4xl font-bold text-white">{completionPct}%</span>
                  <span className="text-white/40 text-sm">Complete</span>
                </div>
              </div>
              <div className="mt-4 text-center">
                <p className="text-white/50 text-sm">
                  {stats?.unlockedAchievements?.toLocaleString()} of {stats?.totalAchievements?.toLocaleString()} achievements
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {insightsData ? (
          <Card className="bg-[#0A0A0A] border-white/5" data-testid="backlog-meter">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
                <PieChartIcon className="w-5 h-5 text-cyan-400" />
                Backlog Meter
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center">
                <div className="h-40 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: "Completed", value: insightsData.backlog?.completed || 0 },
                          { name: "In Progress", value: insightsData.backlog?.started || 0 },
                          { name: "Untouched", value: insightsData.backlog?.untouched || 0 },
                        ]}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={65}
                        paddingAngle={3}
                      >
                        <Cell fill="#22C55E" />
                        <Cell fill="#F59E0B" />
                        <Cell fill="#EF4444" />
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: "rgba(0,0,0,0.95)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px", color: "#fff" }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-5 text-sm mt-2">
                  <span className="text-emerald-400">{insightsData.backlog?.completed} done</span>
                  <span className="text-amber-400">{insightsData.backlog?.started} started</span>
                  <span className="text-red-400">{insightsData.backlog?.untouched} untouched</span>
                </div>
                <p className="text-center text-white/30 text-xs mt-1">{insightsData.backlog?.completionRate}% completion rate</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-[#0A0A0A] border-white/5 animate-pulse" />
        )}
      </div>

      {/* 100% Completions by Platform */}
      {completionsByPlatform?.months?.length > 0 && (
        <Card className="bg-[#0A0A0A] border-white/5" data-testid="completions-by-platform-chart">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
              <Trophy className="w-5 h-5 text-red-400" />
              100% Completions by Platform
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={completionsByPlatform.months}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
                  <XAxis dataKey="month" stroke="#52525B" fontSize={10} tick={{ fill: "#E5E5E5" }} interval={completionsByPlatform.months.length > 24 ? Math.floor(completionsByPlatform.months.length / 12) : 0} />
                  <YAxis stroke="#52525B" fontSize={12} tick={{ fill: "#E5E5E5" }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "rgba(0,0,0,0.95)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px", color: "#fff" }}
                    labelStyle={{ color: "#999" }}
                    formatter={(value, name) => {
                      const capitalized = name.replace(/^(\w+)(_)(games|dlcs)$/i, (_, p, __, t) => 
                        `${p.charAt(0).toUpperCase() + p.slice(1)} ${t.charAt(0).toUpperCase() + t.slice(1)}`
                      );
                      return [value, capitalized];
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {completionsByPlatform.platforms?.map((plat) => (
                    <Bar key={`${plat}_games`} dataKey={`${plat}_games`} name={`${plat.charAt(0).toUpperCase() + plat.slice(1)} Games`} fill={PLATFORM_COLORS[plat] || "#888"} stackId="games" radius={[2, 2, 0, 0]} />
                  ))}
                  {completionsByPlatform.platforms?.map((plat) => (
                    <Bar key={`${plat}_dlcs`} dataKey={`${plat}_dlcs`} name={`${plat.charAt(0).toUpperCase() + plat.slice(1)} DLCs`} fill={PLATFORM_COLORS[plat] || "#888"} fillOpacity={0.4} stackId="dlcs" radius={[2, 2, 0, 0]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-white/40 text-xs text-center mt-2">Game completions (solid) and DLC completions (faded) stacked by platform</p>
          </CardContent>
        </Card>
      )}

      {/* Streaks & Velocity Cards */}
      {streakData && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="streaks-section">
          <Card className="bg-[#0A0A0A] border-white/5 card-hover animate-fade-in-up stagger-1">
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center gap-2 mb-2">
                <Flame className="w-4 h-4 text-orange-400" />
                <span className="text-white/40 text-xs uppercase tracking-wider">Longest Streak</span>
              </div>
              <p className="text-2xl font-bold text-white">{streakData.longestStreak} <span className="text-sm font-normal text-white/30">days</span></p>
              {streakData.longestStreakStart && <p className="text-[10px] text-white/20 mt-1">{streakData.longestStreakStart} → {streakData.longestStreakEnd}</p>}
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/5 card-hover animate-fade-in-up stagger-2">
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-yellow-400" />
                <span className="text-white/40 text-xs uppercase tracking-wider">Current Streak</span>
              </div>
              <p className="text-2xl font-bold text-white">{streakData.currentStreak} <span className="text-sm font-normal text-white/30">days</span></p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/5 card-hover animate-fade-in-up stagger-3">
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
                <span className="text-white/40 text-xs uppercase tracking-wider">This Month</span>
              </div>
              <p className="text-2xl font-bold text-white">{streakData.velocity?.thisMonth}</p>
              <p className={`text-xs mt-1 ${streakData.velocity?.monthTrend >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {streakData.velocity?.monthTrend >= 0 ? "↑" : "↓"} {Math.abs(streakData.velocity?.monthTrend || 0)}% vs last month
              </p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/5 card-hover animate-fade-in-up stagger-4">
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-blue-400" />
                <span className="text-white/40 text-xs uppercase tracking-wider">This Year</span>
              </div>
              <p className="text-2xl font-bold text-white">{streakData.velocity?.thisYear}</p>
              <p className={`text-xs mt-1 ${streakData.velocity?.yearTrend >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {streakData.velocity?.yearTrend >= 0 ? "↑" : "↓"} {Math.abs(streakData.velocity?.yearTrend || 0)}% vs last year
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* === PRO FEATURES BELOW === */}
      <ProGate feature="full_stats" message="Upgrade to Pro for full statistics, charts, milestones & more">

      {/* Completion Speed */}
      <Card className="bg-[#0A0A0A] border-white/5" data-testid="completion-speed">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              Completion Speed
            </CardTitle>
            <Select value={completionSpeedPlatform} onValueChange={setCompletionSpeedPlatform}>
              <SelectTrigger className="w-[140px] bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="All Platforms" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10">
                <SelectItem value="all">All Platforms</SelectItem>
                {platforms.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {completionSpeedStats ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-white/50 text-xs mb-1">Completed Games</p>
                  <p className="text-2xl font-bold text-white">{completionSpeedStats.totalCompletedGames}</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-white/50 text-xs mb-1">Avg. Completion Time</p>
                  <p className="text-2xl font-bold text-emerald-400">
                    {formatDuration(completionSpeedStats.averageDurationHours, completionSpeedStats.averageDurationDays)}
                  </p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-white/50 text-xs mb-1">Avg. Hours</p>
                  <p className="text-2xl font-bold text-cyan-400">{completionSpeedStats.averageDurationHours?.toLocaleString()}h</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-emerald-400 flex items-center gap-2">
                      <Trophy className="w-4 h-4" />
                      Fastest Completions
                    </h4>
                    <Button variant="ghost" size="sm" className="text-white/50 hover:text-white text-xs" onClick={() => { setCompletionSpeedDialogType("fastest"); setCompletionSpeedDialogOpen(true); }}>
                      View All
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {completionSpeedStats.fastest?.slice(0, 5).map((game, index) => (
                      <div key={game.gameId} className="flex items-center gap-3 bg-white/5 rounded-lg p-2 hover:bg-white/10 transition-colors cursor-pointer" onClick={() => window.location.href = `/games/${game.gameId}`}>
                        <span className="text-emerald-400 font-bold w-5 text-center">#{index + 1}</span>
                        {game.boxArtUrl ? (
                          <img src={game.boxArtUrl} alt={game.name} className="w-10 h-10 rounded object-contain bg-black/50" />
                        ) : (
                          <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center"><Gamepad2 className="w-5 h-5 text-white/30" /></div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium truncate">{game.name}</p>
                          <div className="flex items-center gap-2 text-xs text-white/50">
                            <PlatformBadge platform={game.platform} size="xs" />
                            <span>{game.totalAchievements} achievements</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-emerald-400 font-bold text-sm">{formatDuration(game.durationHours, game.durationDays)}</p>
                          <p className="text-white/40 text-xs">{game.achievementsPerHour}/hr</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-orange-400 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Longest Journeys
                    </h4>
                    <Button variant="ghost" size="sm" className="text-white/50 hover:text-white text-xs" onClick={() => { setCompletionSpeedDialogType("slowest"); setCompletionSpeedDialogOpen(true); }}>
                      View All
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {completionSpeedStats.slowest?.slice(0, 5).map((game, index) => (
                      <div key={game.gameId} className="flex items-center gap-3 bg-white/5 rounded-lg p-2 hover:bg-white/10 transition-colors cursor-pointer" onClick={() => window.location.href = `/games/${game.gameId}`}>
                        <span className="text-orange-400 font-bold w-5 text-center">#{index + 1}</span>
                        {game.boxArtUrl ? (
                          <img src={game.boxArtUrl} alt={game.name} className="w-10 h-10 rounded object-contain bg-black/50" />
                        ) : (
                          <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center"><Gamepad2 className="w-5 h-5 text-white/30" /></div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium truncate">{game.name}</p>
                          <div className="flex items-center gap-2 text-xs text-white/50">
                            <PlatformBadge platform={game.platform} size="xs" />
                            <span>{game.totalAchievements} achievements</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-orange-400 font-bold text-sm">{formatDuration(game.durationHours, game.durationDays)}</p>
                          <p className="text-white/40 text-xs">{game.achievementsPerHour}/hr</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-white/30">
              <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-20" />
              <p>Loading completion speed stats...</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Completion Speed Dialog */}
      <Dialog open={completionSpeedDialogOpen} onOpenChange={setCompletionSpeedDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              {completionSpeedDialogType === "fastest" ? (
                <><Trophy className="w-5 h-5 text-emerald-400" />Top 10 Fastest Completions</>
              ) : (
                <><Calendar className="w-5 h-5 text-orange-400" />Top 10 Longest Journeys</>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {(completionSpeedDialogType === "fastest" ? completionSpeedStats?.fastest : completionSpeedStats?.slowest)?.map((game, index) => (
              <div key={game.gameId} className="flex items-center gap-4 bg-white/5 rounded-lg p-3 hover:bg-white/10 transition-colors cursor-pointer" onClick={() => { setCompletionSpeedDialogOpen(false); window.location.href = `/games/${game.gameId}`; }}>
                <span className={`font-bold w-6 text-center ${completionSpeedDialogType === "fastest" ? "text-emerald-400" : "text-orange-400"}`}>#{index + 1}</span>
                {game.boxArtUrl ? (
                  <img src={game.boxArtUrl} alt={game.name} className="w-14 h-14 rounded object-contain bg-black/50" />
                ) : (
                  <div className="w-14 h-14 rounded bg-white/10 flex items-center justify-center"><Gamepad2 className="w-6 h-6 text-white/30" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{game.name}</p>
                  <div className="flex items-center gap-3 text-sm text-white/50 mt-1">
                    <PlatformBadge platform={game.platform} size="sm" />
                    <span>{game.totalAchievements} achievements</span>
                    <span>{game.totalScore?.toLocaleString()} {getScoreAbbr(game.platform)}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-white/40 mt-1">
                    <span>First: {game.firstAchievement?.name}</span>
                    <span>Last: {game.lastAchievement?.name}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-bold text-lg ${completionSpeedDialogType === "fastest" ? "text-emerald-400" : "text-orange-400"}`}>
                    {formatDuration(game.durationHours, game.durationDays)}
                  </p>
                  <p className="text-white/40 text-sm">{game.achievementsPerHour} ach/hr</p>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* ============================================================ */}
      {/* SECTION 2: ACTIVITY & TIME                                    */}
      {/* ============================================================ */}
      <h2 className="text-lg font-heading text-white/60 tracking-wide uppercase mt-4">Activity & Time</h2>

      {/* Monthly Activity Chart */}
      {monthlyData.length > 0 && (
        <Card className="bg-[#0A0A0A] border-white/5">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0 pb-2">
            <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-400" />
              Monthly Activity
            </CardTitle>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-40 bg-[#121212] border-white/10 text-white text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="1month">Last Month</SelectItem>
                <SelectItem value="3months">Last 3 Months</SelectItem>
                <SelectItem value="6months">Last 6 Months</SelectItem>
                <SelectItem value="12months">Last 12 Months</SelectItem>
                <SelectItem value="thisYear">This Year</SelectItem>
                <SelectItem value="lastYear">Last Year</SelectItem>
                <SelectItem value="5years">Last 5 Years</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={monthlyData} onClick={(e) => { if (e?.activeLabel) { const m = e.activeLabel; const parts = m.split(' '); if (parts.length >= 2) openDrilldown("monthly", parts[0]); } }}>
                  <defs>
                    <linearGradient id="statAchievementGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="statScoreGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
                  <XAxis dataKey="month" stroke="#52525B" fontSize={11} tick={{ fill: '#E5E5E5' }} interval={monthlyData.length > 24 ? Math.floor(monthlyData.length / 12) : 0} />
                  <YAxis yAxisId="left" stroke="#52525B" fontSize={12} tick={{ fill: '#E5E5E5' }} />
                  <YAxis yAxisId="right" orientation="right" stroke="#52525B" fontSize={12} tick={{ fill: '#E5E5E5' }} />
                  <Tooltip content={<CustomAreaTooltip />} />
                  <Legend content={renderCustomLegend} wrapperStyle={{ paddingTop: '10px' }} />
                  <Area yAxisId="left" type="monotone" dataKey="achievements" stroke="#10B981" strokeWidth={2} fill="url(#statAchievementGradient)" name="Achievements" hide={!visibleSeries.achievements} />
                  <Area yAxisId="right" type="monotone" dataKey="score" stroke="#F59E0B" strokeWidth={2} fill="url(#statScoreGradient)" name="Score" hide={!visibleSeries.score} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <p className="text-white/40 text-xs text-center mt-2">Click legend items to show/hide data series</p>
          </CardContent>
        </Card>
      )}

      {/* Activity Heatmap */}
      <Card className="bg-[#0A0A0A] border-white/5" data-testid="activity-heatmap">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-400" />
            Activity Heatmap
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Heatmap data={stats?.heatmapData || []} />
        </CardContent>
      </Card>

      {/* Most Active Times Chart */}
      {activeTimesData?.byHour?.some(h => h.count > 0) && (
        <Card className="bg-[#0A0A0A] border-white/5" data-testid="active-times-chart">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-cyan-400" />
              Most Active Times
            </CardTitle>
            <p className="text-white/30 text-xs">When you unlock achievements (excludes batch-synced). Based on your local timezone.</p>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={activeTimesData.byHour} onClick={(e) => { if (e?.activeLabel !== undefined && e?.activeLabel !== null) openDrilldown("hourly", e.activeLabel); }} className="cursor-pointer">
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
                  <XAxis dataKey="hour" stroke="#52525B" fontSize={10} tick={{ fill: "#E5E5E5" }} tickFormatter={(h) => `${h}:00`} />
                  <YAxis stroke="#52525B" fontSize={11} tick={{ fill: "#E5E5E5" }} allowDecimals={false} />
                  <Tooltip content={<ActiveTimesTooltip />} />
                  <Bar dataKey="count" name="Unlocks" radius={[4, 4, 0, 0]} className="cursor-pointer">
                    {activeTimesData.byHour.map((entry, i) => {
                      const maxCount = Math.max(...activeTimesData.byHour.map(h => h.count));
                      const ratio = entry.count / (maxCount || 1);
                      const r = Math.round(6 + ratio * 200);
                      const g = Math.round(182 + ratio * 50);
                      const b = Math.round(212 - ratio * 80);
                      return <Cell key={i} fill={`rgb(${r},${g},${b})`} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-white/30 text-xs text-center mt-1">Click any bar to see minute-by-minute breakdown</p>
          </CardContent>
        </Card>
      )}

      {/* Day-of-Week + Seasonal Patterns */}
      {distributionData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {distributionData.dayOfWeek?.length > 0 && (
            <Card className="bg-[#0A0A0A] border-white/5" data-testid="day-of-week-chart">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-base text-white flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-400" />
                  Your Gaming Day
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={distributionData.dayOfWeek} onClick={(e) => { if (e?.activeLabel) openDrilldown("daily", e.activeLabel); }} className="cursor-pointer">
                      <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
                      <XAxis dataKey="day" stroke="#52525B" fontSize={11} tick={{ fill: "#E5E5E5" }} />
                      <YAxis stroke="#52525B" fontSize={10} tick={{ fill: "#E5E5E5" }} />
                      <Tooltip content={<DayOfWeekTooltip />} />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]} className="cursor-pointer">
                        {distributionData.dayOfWeek.map((entry, i) => {
                          const max = Math.max(...distributionData.dayOfWeek.map(d => d.count));
                          const ratio = entry.count / (max || 1);
                          return <Cell key={i} fill={`rgba(59, 130, 246, ${0.3 + ratio * 0.7})`} />;
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-white/30 text-xs text-center mt-1">Click a day for hourly breakdown</p>
              </CardContent>
            </Card>
          )}
          {distributionData.seasonal?.length > 0 && (
            <Card className="bg-[#0A0A0A] border-white/5" data-testid="seasonal-chart">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-base text-white flex items-center gap-2">
                  <Sun className="w-4 h-4 text-amber-400" />
                  Seasonal Patterns
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={distributionData.seasonal} onClick={(e) => { if (e?.activeLabel) openDrilldown("seasonal", e.activeLabel); }} className="cursor-pointer">
                      <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
                      <XAxis dataKey="season" stroke="#52525B" fontSize={11} tick={{ fill: "#E5E5E5" }} />
                      <YAxis stroke="#52525B" fontSize={10} tick={{ fill: "#E5E5E5" }} />
                      <Tooltip content={<SeasonalTooltip />} />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]} className="cursor-pointer">
                        <Cell fill="#60A5FA" />
                        <Cell fill="#34D399" />
                        <Cell fill="#FBBF24" />
                        <Cell fill="#F97316" />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Best Days Chart */}
      <Card className="bg-[#0A0A0A] border-white/5" data-testid="best-days-chart">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0 pb-2">
          <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-400" />
            Best Days by
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={bestDaysCategory} onValueChange={setBestDaysCategory}>
              <SelectTrigger className="w-32 bg-[#121212] border-white/10 text-white text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="score">Score</SelectItem>
                <SelectItem value="unlocks">Unlocks</SelectItem>
                <SelectItem value="completions">Completions</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={openBestDaysDialog} className="bg-[#121212] border-white/10 text-white hover:bg-white/10">
              <ExternalLink className="w-4 h-4 mr-1" />
              View All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {bestDaysData.length > 0 ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bestDaysData} layout="vertical" onClick={(e) => { const d = e?.activePayload?.[0]?.payload; if (d?.date) { setBestDayDate(d.date); setBestDayOpen(true); } }} className="cursor-pointer">
                  <XAxis type="number" stroke="#A1A1AA" fontSize={12} tick={{ fill: '#E5E5E5' }} />
                  <YAxis dataKey="date" type="category" stroke="#A1A1AA" fontSize={11} width={90} tick={{ fill: '#E5E5E5' }}
                    tickFormatter={(value) => { try { return format(new Date(value), "MMM d, yyyy"); } catch { return value; } }}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: "rgba(0,0,0,0.95)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px", color: "#fff" }}
                    formatter={(value) => [formatBestDaysValue(value), getBestDaysLabel()]}
                    labelFormatter={(label) => { try { return format(new Date(label), "EEEE, MMMM d, yyyy"); } catch { return label; } }}
                  />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} fill={BEST_DAYS_COLORS[bestDaysCategory]}
                    cursor="pointer"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-white/50">No data available</div>
          )}
        </CardContent>
      </Card>

      {/* Best Days Dialog */}
      <Dialog open={bestDaysDialogOpen} onOpenChange={setBestDaysDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-400" />
              Best Days by {getBestDaysLabel()}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2 -mr-2">
            <div className="space-y-2">
              {fullBestDaysData.map((day, index) => (
                <div key={day.date} className="flex items-center justify-between p-3 bg-[#121212] rounded-lg border border-white/5 hover:border-white/10 transition-colors cursor-pointer"
                  onClick={() => { setBestDaysDialogOpen(false); setBestDayDate(day.date); setBestDayOpen(true); }}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      index === 0 ? 'bg-yellow-500 text-black' : index === 1 ? 'bg-gray-300 text-black' : index === 2 ? 'bg-amber-600 text-white' : 'bg-white/10 text-white/70'
                    }`}>{index + 1}</div>
                    <div>
                      <p className="text-white font-medium">{(() => { try { return format(new Date(day.date), "EEEE, MMMM d, yyyy"); } catch { return day.date; } })()}</p>
                      {day.count && bestDaysCategory === "score" && <p className="text-white/40 text-sm">{day.count} achievements</p>}
                      {day.count && bestDaysCategory === "unlocks" && <p className="text-white/40 text-sm">{day.count.toLocaleString()} pts</p>}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold" style={{ color: BEST_DAYS_COLORS[bestDaysCategory] }}>{formatBestDaysValue(day.value)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Gaming Personality */}
      {insightsData && (
        <Card className="bg-[#0A0A0A] border-white/5" data-testid="personality-card">
          <CardContent className="pt-5 pb-4 px-5">
            <div className="flex items-center gap-3 mb-3">
              {insightsData.personality === "Night Owl" ? <Moon className="w-6 h-6 text-indigo-400" /> :
               insightsData.personality === "Early Bird" ? <Sun className="w-6 h-6 text-amber-400" /> :
               <Clock className="w-6 h-6 text-orange-400" />}
              <div>
                <p className="text-lg font-bold text-white">{insightsData.personality}</p>
                <p className="text-xs text-white/30">Your gaming personality</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
              {[
                { label: "Early Bird", value: insightsData.timeSlots?.earlyBird, sub: "5am-12pm", color: "text-amber-400" },
                { label: "Afternoon", value: insightsData.timeSlots?.afternoon, sub: "12pm-5pm", color: "text-orange-400" },
                { label: "Evening", value: insightsData.timeSlots?.evening, sub: "5pm-10pm", color: "text-rose-400" },
                { label: "Night Owl", value: insightsData.timeSlots?.nightOwl, sub: "10pm-5am", color: "text-indigo-400" },
              ].map((slot) => (
                <div key={slot.label} className="text-center">
                  <p className={`text-sm font-bold ${slot.color}`}>{slot.value?.toLocaleString()}</p>
                  <p className="text-[9px] text-white/20">{slot.sub}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ============================================================ */}
      {/* SECTION 3: PROFILE & IDENTITY                                 */}
      {/* ============================================================ */}
      <h2 className="text-lg font-heading text-white/60 tracking-wide uppercase mt-4">Profile & Identity</h2>

      {/* Score by Platform + Achievement Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-[#0A0A0A] border-white/5" data-testid="score-by-platform">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-white">Score by Platform</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {platformData.filter(p => p.score > 0).sort((a, b) => b.score - a.score).map((platform) => (
              <div key={platform.platform} className="flex items-center justify-between p-3 bg-[#121212] rounded-lg border border-white/5 cursor-pointer hover:border-white/20 transition-colors" onClick={() => openDrilldown("platform", platform.platform)} data-testid={`platform-drilldown-${platform.platform}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${platform.color}20` }}>
                    <Gamepad2 className="w-5 h-5" style={{ color: platform.color }} />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">{platform.name}</p>
                    <p className="text-white/40 text-xs">{platform.count} achievements</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-yellow-400 font-bold font-mono">{platform.score.toLocaleString()}</p>
                  <p className="text-yellow-400/50 text-xs">{getScoreAbbr(platform.platform)}</p>
                </div>
              </div>
            ))}
            {platformData.filter(p => p.score > 0).length === 0 && (
              <div className="text-center py-8 text-white/30"><p>No score data yet</p></div>
            )}
            {platformData.filter(p => p.score > 0).length > 0 && (
              <p className="text-white/30 text-xs text-center mt-2">Click a platform for system breakdown</p>
            )}
          </CardContent>
        </Card>

        <Card className="bg-[#0A0A0A] border-white/5" data-testid="achievement-distribution">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-white">Achievement Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <div className="flex flex-col items-center">
                <div className="w-full h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2} dataKey="count" nameKey="name">
                        {pieData.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.color} />))}
                      </Pie>
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            const total = pieData.reduce((sum, d) => sum + d.count, 0);
                            const percent = ((data.count / total) * 100).toFixed(1);
                            return (
                              <div className="bg-black/95 border border-white/20 rounded-lg px-3 py-2">
                                <p className="text-white font-medium flex items-center gap-2">
                                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: data.color }} />{data.name}
                                </p>
                                <p className="text-white/70 text-sm">{data.count.toLocaleString()} achievements</p>
                                <p className="text-white/50 text-xs">{percent}% of total</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-3">
                  {pieData.map((entry) => {
                    const total = pieData.reduce((sum, d) => sum + d.count, 0);
                    const percent = ((entry.count / total) * 100).toFixed(0);
                    return (
                      <div key={entry.name} className="flex items-center gap-2 cursor-pointer hover:bg-white/5 rounded px-1 py-0.5 transition-colors" onClick={() => openDrilldown("platform", entry.platform || entry.name.toLowerCase())} data-testid={`pie-drilldown-${entry.name}`}>
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
                        <span className="text-white/80 text-sm">{entry.name}</span>
                        <span className="text-white/40 text-xs ml-auto">{entry.count.toLocaleString()} ({percent}%)</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="h-56 flex items-center justify-center text-white/30">No data yet</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Platform Loyalty Over Time */}
      {insightsData?.platformLoyalty?.length > 0 && (
        <Card className="bg-[#0A0A0A] border-white/5" data-testid="platform-loyalty-chart">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
              <Gamepad2 className="w-5 h-5 text-green-400" />
              Platform Loyalty Over Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={insightsData.platformLoyalty} onClick={(e) => { if (e?.activeLabel) openDrilldown("yearly", String(e.activeLabel)); }} className="cursor-pointer">
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
                  <XAxis dataKey="year" stroke="#52525B" fontSize={10} tick={{ fill: "#E5E5E5" }} />
                  <YAxis stroke="#52525B" fontSize={11} tick={{ fill: "#E5E5E5" }} />
                  <Tooltip contentStyle={{ backgroundColor: "rgba(0,0,0,0.95)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px", color: "#fff" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {insightsData.loyaltyPlatforms?.map((plat, i) => (
                    <Line key={plat} type="monotone" dataKey={plat} name={plat.charAt(0).toUpperCase() + plat.slice(1)} stroke={PLATFORM_COLORS[plat] || ["#8B5CF6", "#06B6D4", "#F59E0B"][i % 3]} strokeWidth={2} dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Milestone Timeline — Split by Type */}
      {streakData?.milestones?.length > 0 && (() => {
        const achMs = (streakData.milestones || []).filter(m => m.type === "achievement").sort((a, b) => a.date.localeCompare(b.date));
        const scoreMs = (streakData.milestones || []).filter(m => m.type === "score").sort((a, b) => a.date.localeCompare(b.date));
        const tagMs = (streakData.milestones || []).filter(m => m.type === "tag").sort((a, b) => a.date.localeCompare(b.date));
        const compMs = [...(streakData.completionMilestones || [])].sort((a, b) => a.date.localeCompare(b.date));
        const nextMs = streakData.nextMilestones || {};
        const colCount = 3 + (tagMs.length > 0 || nextMs.tag ? 1 : 0);

        // Build consolidated timeline — merge all milestones with type label
        const allTimelineMilestones = [
          ...achMs.map(m => ({ ...m, _type: "achievement" })),
          ...scoreMs.map(m => ({ ...m, _type: "score" })),
          ...compMs.map(m => ({ ...m, _type: "completion" })),
          ...tagMs.map(m => ({ ...m, _type: "tag" })),
        ].sort((a, b) => a.date.localeCompare(b.date));

        const typeColor = { achievement: "#8B5CF6", score: "#F59E0B", completion: "#EF4444", tag: "#F97316" };
        const typeIcon = { achievement: Trophy, score: Star, completion: Gamepad2, tag: Tag };
        const typeLabel = (m) => {
          if (m._type === "achievement") return achMilestoneLabel(m);
          if (m._type === "score") return scoreMilestoneLabel(m);
          if (m._type === "completion") return compMilestoneLabel(m);
          if (m._type === "tag") return tagMilestoneLabel(m);
          return "";
        };

        const renderNextGoal = (nm, type, color) => {
          if (!nm) return null;
          return (
            <div className="flex items-start gap-3 pl-1 opacity-30 mt-1">
              <div className="relative z-10 w-7 h-7 rounded-full flex items-center justify-center shrink-0 border border-dashed border-white/30">
                <Target className="w-3 h-3 text-white/50" />
              </div>
              <div className="w-9 h-9 rounded flex items-center justify-center shrink-0 border border-dashed border-white/10 bg-white/[0.02]">
                <span className="text-[10px] text-white/30">?</span>
              </div>
              <div className="flex-1 min-w-0">
                {nm.targetName && <p className="text-sm text-white/60 font-semibold">{nm.targetName}</p>}
                <p className="text-sm text-white/60 font-medium">Next Closest Milestone</p>
                <p className="text-xs text-white/40">{nextMilestoneLabel(nm, type)}</p>
                <p className="text-[10px] text-white/20">{nm.current.toLocaleString()} / {nm.target.toLocaleString()} ({nm.remaining.toLocaleString()} to go)</p>
              </div>
            </div>
          );
        };

        return (
        <div className="space-y-4">
        {/* Consolidated Horizontal Timeline */}
        {allTimelineMilestones.length > 0 && (
          <Card className="bg-[#0A0A0A] border-white/5" data-testid="milestone-horizontal-timeline">
            <CardHeader className="pb-2">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-cyan-400" />
                  Milestone Timeline
                </CardTitle>
                <div className="flex flex-wrap items-center gap-2 text-[10px] text-white/40">
                  {[{ t: "achievement", c: "#8B5CF6", l: "Achievement" }, { t: "score", c: "#F59E0B", l: "Score" }, { t: "completion", c: "#EF4444", l: "Completion" }, { t: "tag", c: "#F97316", l: "Tag" }].map(leg => (
                    <span key={leg.t} className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: leg.c }} />{leg.l}</span>
                  ))}
                </div>
              </div>
              <p className="text-white/30 text-xs">{allTimelineMilestones.length} milestones in chronological order &middot; scroll to explore</p>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <div className="overflow-x-auto pb-4 scrollbar-thin" data-testid="timeline-scroll-container">
                  <div className="flex items-start gap-0 min-w-max relative" style={{ paddingTop: "12px" }}>
                    {/* Timeline line */}
                    <div className="absolute top-[30px] left-0 right-0 h-px bg-white/10" />
                    {allTimelineMilestones.map((m, i) => {
                      const Icon = typeIcon[m._type] || Trophy;
                      const color = typeColor[m._type] || "#8B5CF6";
                      const mKey = m._type === "completion" ? makeMilestoneKey(m, "comp") : makeMilestoneKey(m, m._type);
                      const isHatched = hatchedMilestones.has(mKey);
                      return (
                        <div
                          key={`tl-${i}`}
                          ref={isHatched ? hatchRef(`tl-${mKey}`) : undefined}
                          className={`flex flex-col items-center w-[120px] shrink-0 relative group ${isHatched ? "animate-milestone-hatch" : ""}`}
                          data-testid={`timeline-milestone-${i}`}
                        >
                          {/* Connector dot on the line */}
                          <div className="w-3 h-3 rounded-full border-2 z-10 mb-2 transition-transform group-hover:scale-150" style={{ borderColor: color, backgroundColor: `${color}40` }} />
                          {/* Card */}
                          <div className="px-2 py-2 rounded-lg bg-white/[0.03] border border-white/5 hover:border-white/15 transition-all w-[108px] text-center group-hover:bg-white/[0.06]">
                            <div className="flex justify-center mb-1.5">
                              {m.imageUrl ? (
                                <img src={m.imageUrl} alt="" className="w-7 h-7 rounded object-contain bg-black/50" />
                              ) : (
                                <div className="w-7 h-7 rounded flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                                  <Icon className="w-3.5 h-3.5" style={{ color }} />
                                </div>
                              )}
                            </div>
                            <p className="text-[10px] font-medium text-white truncate leading-tight">{m.targetName || typeLabel(m)}</p>
                            <p className="text-[9px] text-white/40 truncate mt-0.5">{m.name}</p>
                            <p className="text-[8px] text-white/20 mt-0.5">{m.date}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* By Type Cards */}
        <div className={`grid grid-cols-1 md:grid-cols-2 ${colCount >= 4 ? "xl:grid-cols-4" : "xl:grid-cols-3"} gap-4`} data-testid="milestone-by-type">
          {/* Achievement Milestones */}
          <Card className="bg-[#0A0A0A] border-white/5">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base text-white flex items-center gap-2">
                <Trophy className="w-4 h-4 text-purple-400" />
                Achievement Milestones
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative max-h-[600px] overflow-y-auto pr-1">
                <div className="absolute left-4 top-0 bottom-0 w-px bg-purple-500/20" />
                <div className="space-y-3">
                  {achMs.map((m, i) => {
                    const isSystem = !!m.system;
                    const isPlatform = !isSystem && !!m.platform;
                    const accentColor = (isSystem || isPlatform) ? (PLATFORM_COLORS[m.platform] || "#7C3AED") : "#7C3AED";
                    const mKey = makeMilestoneKey(m, "achievement");
                    const isHatched = hatchedMilestones.has(mKey);
                    return (
                    <div key={i} ref={isHatched ? hatchRef(mKey) : undefined} className={`flex items-start gap-3 pl-1 ${isHatched ? "animate-milestone-hatch" : ""}`}>
                      <div className="relative z-10 w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${accentColor}20`, border: `1px solid ${accentColor}40` }}>
                        {isSystem ? (
                          <span className="text-[6px] font-bold leading-none" style={{ color: accentColor }}>{sysAbbrev(m.system)}</span>
                        ) : isPlatform ? (
                          <span className="text-[8px] font-bold" style={{ color: accentColor }}>{m.platform?.slice(0,2).toUpperCase()}</span>
                        ) : (
                          <Trophy className="w-3 h-3 text-purple-400" />
                        )}
                      </div>
                      {m.imageUrl ? (
                        <img src={m.imageUrl} alt="" className="w-9 h-9 rounded object-contain bg-black/50 shrink-0" />
                      ) : (
                        <div className="w-9 h-9 rounded flex items-center justify-center shrink-0" style={{ backgroundColor: `${accentColor}10` }}>
                          <Award className="w-4 h-4" style={{ color: `${accentColor}66` }} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        {m.targetName && <p className="text-sm text-white font-semibold">{m.targetName}</p>}
                        <p className={`text-white ${m.targetName ? "text-xs text-white/50" : "text-sm font-medium"}`}>{achMilestoneLabel(m)}</p>
                        <p className="text-xs text-white/50 truncate">{m.name}</p>
                        <p className="text-[10px] text-white/25">{m.gameName} &middot; {m.date}</p>
                      </div>
                    </div>
                    );
                  })}
                  {renderNextGoal(nextMs.achievement, "achievement", "#7C3AED")}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Score Milestones */}
          <Card className="bg-[#0A0A0A] border-white/5">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base text-white flex items-center gap-2">
                <Star className="w-4 h-4 text-amber-400" />
                Score Milestones
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative max-h-[600px] overflow-y-auto pr-1">
                <div className="absolute left-4 top-0 bottom-0 w-px bg-amber-500/20" />
                <div className="space-y-3">
                  {scoreMs.map((m, i) => {
                    const isSystem = !!m.system;
                    const isPlatform = !isSystem && !!m.platform;
                    const accentColor = (isSystem || isPlatform) ? (PLATFORM_COLORS[m.platform] || "#F59E0B") : "#F59E0B";
                    const mKey = makeMilestoneKey(m, "score");
                    const isHatched = hatchedMilestones.has(mKey);
                    return (
                    <div key={i} ref={isHatched ? hatchRef(mKey) : undefined} className={`flex items-start gap-3 pl-1 ${isHatched ? "animate-milestone-hatch" : ""}`}>
                      <div className="relative z-10 w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${accentColor}20`, border: `1px solid ${accentColor}40` }}>
                        {isSystem ? (
                          <span className="text-[6px] font-bold leading-none" style={{ color: accentColor }}>{sysAbbrev(m.system)}</span>
                        ) : isPlatform ? (
                          <span className="text-[8px] font-bold" style={{ color: accentColor }}>{m.platform?.slice(0,2).toUpperCase()}</span>
                        ) : (
                          <Star className="w-3 h-3 text-amber-400" />
                        )}
                      </div>
                      {m.imageUrl ? (
                        <img src={m.imageUrl} alt="" className="w-9 h-9 rounded object-contain bg-black/50 shrink-0" />
                      ) : (
                        <div className="w-9 h-9 rounded flex items-center justify-center shrink-0" style={{ backgroundColor: `${accentColor}10` }}>
                          <Star className="w-4 h-4" style={{ color: `${accentColor}66` }} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        {m.targetName && <p className="text-sm text-white font-semibold">{m.targetName}</p>}
                        <p className={`text-white ${m.targetName ? "text-xs text-white/50" : "text-sm font-medium"}`}>{scoreMilestoneLabel(m)}</p>
                        <p className="text-xs text-white/50 truncate">{m.name}</p>
                        <p className="text-[10px] text-white/25">{m.gameName} &middot; {m.date}</p>
                      </div>
                    </div>
                    );
                  })}
                  {renderNextGoal(nextMs.score, "score", "#F59E0B")}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Completion Milestones */}
          {compMs.length > 0 && (
            <Card className="bg-[#0A0A0A] border-white/5">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-base text-white flex items-center gap-2">
                  <Gamepad2 className="w-4 h-4 text-emerald-400" />
                  Completion Milestones
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative max-h-[600px] overflow-y-auto pr-1">
                  <div className="absolute left-4 top-0 bottom-0 w-px bg-emerald-500/20" />
                  <div className="space-y-3">
                    {compMs.map((m, i) => {
                      const isSystemScope = m.scope === "system";
                      const isPlatformScope = m.scope === "platform";
                      const isDlcScope = m.scope === "dlc";
                      const accentColor = (isSystemScope || isPlatformScope || isDlcScope)
                        ? (PLATFORM_COLORS[m.platform] || "#10B981")
                        : "#10B981";
                      const mKey = makeMilestoneKey(m, "comp");
                      const isHatched = hatchedMilestones.has(mKey);
                      return (
                      <div key={i} ref={isHatched ? hatchRef(mKey) : undefined} className={`flex items-start gap-3 pl-1 ${isHatched ? "animate-milestone-hatch" : ""}`}>
                        <div className="relative z-10 w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${accentColor}20`, border: `1px solid ${accentColor}40` }}>
                          {isDlcScope ? (
                            <span className="text-[7px] font-bold" style={{ color: accentColor }}>DLC</span>
                          ) : isSystemScope ? (
                            <span className="text-[6px] font-bold leading-none" style={{ color: accentColor }}>{sysAbbrev(m.system)}</span>
                          ) : isPlatformScope ? (
                            <span className="text-[8px] font-bold" style={{ color: accentColor }}>{m.platform?.slice(0,2).toUpperCase()}</span>
                          ) : (
                            <Gamepad2 className="w-3 h-3 text-emerald-400" />
                          )}
                        </div>
                        {m.imageUrl ? (
                          <img src={m.imageUrl} alt="" className="w-9 h-9 rounded object-contain bg-black/50 shrink-0" />
                        ) : (
                          <div className="w-9 h-9 rounded flex items-center justify-center shrink-0" style={{ backgroundColor: `${accentColor}10` }}>
                            <Gamepad2 className="w-4 h-4" style={{ color: `${accentColor}66` }} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          {m.targetName && <p className="text-sm text-white font-semibold">{m.targetName}</p>}
                          <p className={`text-white ${m.targetName ? "text-xs text-white/50" : "text-sm font-medium"}`}>{compMilestoneLabel(m)}</p>
                          <p className="text-xs text-white/50 truncate">{m.gameName}</p>
                          <p className="text-[10px] text-white/25 capitalize">{!isPlatformScope && !isDlcScope && !isSystemScope ? m.platform + " \u00b7 " : ""}{m.date}</p>
                        </div>
                      </div>
                      );
                    })}
                    {renderNextGoal(nextMs.completion, "completion", "#10B981")}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tag Milestones */}
          {(tagMs.length > 0 || nextMs.tag) && (
            <Card className="bg-[#0A0A0A] border-white/5">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-base text-white flex items-center gap-2" data-testid="tag-milestones-title">
                  <Tag className="w-4 h-4 text-teal-400" />
                  Tag Milestones
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative max-h-[600px] overflow-y-auto pr-1">
                  <div className="absolute left-4 top-0 bottom-0 w-px bg-teal-500/20" />
                  <div className="space-y-3">
                    {tagMs.map((m, i) => {
                      const mKey = makeMilestoneKey(m, "tag");
                      const isHatched = hatchedMilestones.has(mKey);
                      return (
                      <div key={i} ref={isHatched ? hatchRef(mKey) : undefined} className={`flex items-start gap-3 pl-1 ${isHatched ? "animate-milestone-hatch" : ""}`} data-testid={`tag-milestone-${i}`}>
                        <div className="relative z-10 w-7 h-7 rounded-full flex items-center justify-center shrink-0 bg-teal-500/10 border border-teal-500/30">
                          <Tag className="w-3 h-3 text-teal-400" />
                        </div>
                        {m.imageUrl ? (
                          <img src={m.imageUrl} alt="" className="w-9 h-9 rounded object-contain bg-black/50 shrink-0" />
                        ) : (
                          <div className="w-9 h-9 rounded flex items-center justify-center shrink-0 bg-teal-500/10">
                            <Award className="w-4 h-4 text-teal-400/40" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          {m.targetName && <p className="text-sm text-white font-semibold">{m.targetName}</p>}
                          <p className={`text-white ${m.targetName ? "text-xs text-white/50" : "text-sm font-medium"}`}>{tagMilestoneLabel(m)}</p>
                          <p className="text-xs text-white/50 truncate">{m.name}</p>
                          <p className="text-[10px] text-white/25">{m.gameName} &middot; {m.date}</p>
                        </div>
                      </div>
                      );
                    })}
                    {renderNextGoal(nextMs.tag, "tag", "#14B8A6")}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        </div>
        );
      })()}

      {/* ============================================================ */}
      {/* SECTION 4: DEEP DIVES                                         */}
      {/* ============================================================ */}
      <h2 className="text-lg font-heading text-white/60 tracking-wide uppercase mt-4">Deep Dives</h2>

      {/* Favorite Genres + Least Played Genres */}
      {genreData?.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="bg-[#0A0A0A] border-white/5" data-testid="genre-chart">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-amber-400" />
                Favorite Genres
              </CardTitle>
              <p className="text-white/30 text-xs">Based on genre tags across your game library</p>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={genreData.slice(0, 12)} layout="vertical" onClick={(e) => { const d = e?.activePayload?.[0]?.payload; if (d?.genre) { setGenreName(d.genre); setGenreOpen(true); } }} className="cursor-pointer">
                    <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
                    <XAxis type="number" stroke="#52525B" fontSize={11} tick={{ fill: "#E5E5E5" }} allowDecimals={false} />
                    <YAxis dataKey="genre" type="category" stroke="#52525B" fontSize={10} width={100} tick={{ fill: "#E5E5E5" }} />
                    <Tooltip content={<GenreTooltip />} />
                    <Bar dataKey="count" name="Games" radius={[0, 4, 4, 0]}
                      cursor="pointer"
                    >
                      {genreData.slice(0, 12).map((_, i) => {
                        const colors = ["#F59E0B", "#EF4444", "#10B981", "#3B82F6", "#8B5CF6", "#EC4899", "#06B6D4", "#F97316", "#22C55E", "#6366F1", "#14B8A6", "#E11D48"];
                        return <Cell key={i} fill={colors[i % colors.length]} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/5" data-testid="least-played-genres-chart">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-rose-400" />
                Least Played Genres
              </CardTitle>
              <p className="text-white/30 text-xs">Genres in your library you play the least</p>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[...genreData].filter(g => g.count > 0).reverse().slice(0, 12)} layout="vertical" onClick={(e) => { const d = e?.activePayload?.[0]?.payload; if (d?.genre) { setGenreName(d.genre); setGenreOpen(true); } }} className="cursor-pointer">
                    <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
                    <XAxis type="number" stroke="#52525B" fontSize={11} tick={{ fill: "#E5E5E5" }} allowDecimals={false} />
                    <YAxis dataKey="genre" type="category" stroke="#52525B" fontSize={10} width={100} tick={{ fill: "#E5E5E5" }} />
                    <Tooltip content={<GenreTooltip />} />
                    <Bar dataKey="count" name="Games" radius={[0, 4, 4, 0]}
                      cursor="pointer"
                    >
                      {[...genreData].filter(g => g.count > 0).reverse().slice(0, 12).map((_, i) => {
                        const colors = ["#F43F5E", "#FB7185", "#FDA4AF", "#E879F9", "#C084FC", "#A78BFA", "#818CF8", "#93C5FD", "#67E8F9", "#5EEAD4", "#86EFAC", "#BEF264"];
                        return <Cell key={i} fill={colors[i % colors.length]} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Score Distribution */}
      {distributionData?.scoreDistribution?.length > 0 && (
        <Card className="bg-[#0A0A0A] border-white/5" data-testid="score-distribution-chart">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
              <Gem className="w-5 h-5 text-pink-400" />
              Achievement Score Distribution
            </CardTitle>
            <p className="text-white/30 text-xs">How your achievements break down by point value</p>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={distributionData.scoreDistribution} onClick={(e) => { if (e?.activeLabel) openDrilldown("score_range", e.activeLabel); }} className="cursor-pointer">
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
                  <XAxis dataKey="range" stroke="#52525B" fontSize={10} tick={{ fill: "#E5E5E5" }} />
                  <YAxis stroke="#52525B" fontSize={10} tick={{ fill: "#E5E5E5" }} />
                  <Tooltip content={<ScoreDistTooltip />} />
                  <Bar dataKey="count" name="Achievements" radius={[4, 4, 0, 0]} className="cursor-pointer">
                    {distributionData.scoreDistribution.map((_, i) => {
                      const colors = ["#6366F1", "#818CF8", "#A78BFA", "#C084FC", "#D946EF", "#F472B6", "#FB7185", "#F87171", "#FBBF24"];
                      return <Cell key={i} fill={colors[i % colors.length]} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-white/30 text-xs text-center mt-1">Click a bar to see per-score breakdown</p>
          </CardContent>
        </Card>
      )}

      {/* Rarest Achievement Showcase */}
      {distributionData?.rarest?.length > 0 && (
        <Card className="bg-[#0A0A0A] border-purple-500/20" data-testid="rarest-achievements">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
              <Award className="w-5 h-5 text-purple-400" />
              Rarest Achievement Showcase
            </CardTitle>
            <p className="text-white/30 text-xs">Your most exclusive unlocks by global rarity</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {distributionData.rarest.map((r, i) => (
                <div key={i} className="flex items-center gap-3 py-2 px-3 rounded-lg bg-white/[0.02] hover:bg-white/[0.05] transition-colors">
                  <span className="text-white/20 text-xs font-bold w-5 text-right">{i + 1}</span>
                  {r.imageUrl ? (
                    <img src={r.imageUrl} alt="" className="w-8 h-8 rounded" />
                  ) : (
                    <div className="w-8 h-8 rounded bg-purple-600/20 flex items-center justify-center"><Award className="w-4 h-4 text-purple-400" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium truncate">{r.name}</p>
                    <p className="text-[10px] text-white/30 truncate capitalize">{r.gameName} &middot; {r.platform}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-purple-400">{r.rarity}%</p>
                    {r.score > 0 && <p className="text-[10px] text-white/20">{r.score}G</p>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Games by Category */}
      <Card className="bg-[#0A0A0A] border-white/5" data-testid="top-games-chart">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0 pb-2">
          <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-400" />
            Top Games by
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={topGamesCategory} onValueChange={setTopGamesCategory}>
              <SelectTrigger className="w-32 bg-[#121212] border-white/10 text-white text-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="score">Score</SelectItem>
                <SelectItem value="unlocks">Unlocks</SelectItem>
                <SelectItem value="completion">Completion</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => setTopGamesDialogOpen(true)} className="bg-[#121212] border-white/10 text-white hover:bg-white/10">
              <ExternalLink className="w-4 h-4 mr-1" />View All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {topGamesData.length > 0 ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topGamesData} layout="vertical" onClick={(e) => { const d = e?.activePayload?.[0]?.payload; if (d?.id) navigate(`/games/${d.id}`); }} className="cursor-pointer">
                  <XAxis type="number" stroke="#A1A1AA" fontSize={12} tick={{ fill: '#E5E5E5' }} domain={getXAxisDomain()} />
                  <YAxis dataKey="name" type="category" stroke="#A1A1AA" fontSize={11} width={110} tick={{ fill: '#E5E5E5' }} />
                  <Tooltip contentStyle={{ backgroundColor: "rgba(0,0,0,0.95)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px", color: "#fff" }} formatter={getTooltipFormatter()} />
                  <Bar dataKey={getDataKey()} radius={[0, 4, 4, 0]} cursor="pointer">
                    {topGamesData.map((entry, index) => (<Cell key={`cell-${index}`} fill={PLATFORM_COLORS[entry.platform] || "#7C3AED"} />))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-white/50">No game data yet</div>
          )}
          <p className="text-white/30 text-xs text-center mt-2">Click any bar to open the game detail page</p>
        </CardContent>
      </Card>

      {/* Top Games Dialog */}
      <Dialog open={topGamesDialogOpen} onOpenChange={setTopGamesDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-400" />
              Top Games by {getTopGamesCategoryLabel()}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2 -mr-2">
            <div className="space-y-2">
              {getFullTopGamesData().map((game, index) => (
                <div key={`${game.fullName}-${index}`} className="flex items-center justify-between p-3 bg-[#121212] rounded-lg border border-white/5 hover:border-white/10 transition-colors cursor-pointer" onClick={() => { setTopGamesDialogOpen(false); navigate(`/games/${game.id}`); }} data-testid={`top-game-${index}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      index === 0 ? 'bg-yellow-500 text-black' : index === 1 ? 'bg-gray-300 text-black' : index === 2 ? 'bg-amber-600 text-white' : 'bg-white/10 text-white/70'
                    }`}>{index + 1}</div>
                    {game.boxArtUrl ? (
                      <img src={game.boxArtUrl} alt={game.fullName} className="w-10 h-14 rounded object-contain bg-black/50" />
                    ) : (
                      <div className="w-10 h-14 rounded bg-white/5 flex items-center justify-center"><Gamepad2 className="w-5 h-5 text-white/20" /></div>
                    )}
                    <div>
                      <p className="text-white font-medium">{game.fullName}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <PlatformBadge platform={game.platform} size="sm" />
                        {topGamesCategory === "score" && <span className="text-white/40 text-sm">{game.unlocks}/{game.totalAchievements} achievements</span>}
                        {topGamesCategory === "unlocks" && <span className="text-white/40 text-sm">{game.score.toLocaleString()} pts</span>}
                        {topGamesCategory === "completion" && <span className="text-white/40 text-sm">{game.unlocks}/{game.totalAchievements} achievements</span>}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold" style={{ color: PLATFORM_COLORS[game.platform] || "#7C3AED" }}>{formatTopGamesValue(game)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AdSense */}
      <AdBanner />

      {/* Drilldown Dialog */}
      <StatsDrilldownDialog
        open={drilldown.open}
        onOpenChange={(open) => !open && closeDrilldown()}
        chartType={drilldown.chartType}
        value={drilldown.value}
        tzOffset={tzOffset}
        platform={selectedPlatform !== "all" ? selectedPlatform : undefined}
      />

      </ProGate>
    </div>
  );
}
